import React from "react";
import { Box, Button, IconButton, Radio, Typography } from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { conLugares, paquetesQueEntran } from "../../domain/compra/pedido";
import { formatoPrecio } from "../mapa/PanelTarifas";
import { COLOR } from "../mapa/estilos";

export const Cantidad = ({ valor, maximo, onCambiar }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <IconButton size="small" onClick={() => onCambiar(valor - 1)} disabled={valor <= 0} aria-label="Quitar uno">
      <Remove fontSize="small" />
    </IconButton>
    <Typography sx={{ minWidth: 20, textAlign: "center", fontWeight: 600 }}>{valor}</Typography>
    <IconButton size="small" onClick={() => onCambiar(valor + 1)} disabled={valor >= maximo} aria-label="Sumar uno">
      <Add fontSize="small" />
    </IconButton>
  </Box>
);

const Renglon = ({ titulo, detalle, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, py: 0.75 }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, color: COLOR.titulo }}>{titulo}</Typography>
      {detalle && <Typography sx={{ fontSize: 12, color: COLOR.textoSuave }}>{detalle}</Typography>}
    </Box>
    {children}
  </Box>
);

const Titulo = ({ children }) => (
  <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLOR.titulo, mt: 2, mb: 0.5 }}>{children}</Typography>
);

// PanelMesa: para cuántas personas es la mesa, sus adicionales y las
// entradas que se sugieren sumar.
const PanelMesa = ({ indice, mesa, tarifa, valor, yaElegida, onCambiar, onConfirmar, onQuitar, onCancelar }) => {
  const paquetes = paquetesQueEntran(tarifa, mesa);
  const sinLugar = tarifa.paquetes.length > 0 && paquetes.length === 0;
  const faltaPaquete = tarifa.paquetes.length > 0 && !valor.idpaquete;
  const adicionales = tarifa.adicionales.map((a) => ({ ...a, tarifa: indice.tarifas[a.idtarifa] })).filter((a) => a.tarifa);
  const ofrecidas = tarifa.sugeridas
    .map((id) => indice.tarifas[id])
    .filter((t) => t && t.disponible && !conLugares(indice.sectores[t.idubicacion] || { filas: [], mesas: [] }));

  return (
    <Box>
      <Typography sx={{ fontSize: 17, fontWeight: 700, color: COLOR.titulo }}>{mesa.nombre}</Typography>
      <Typography sx={{ fontSize: 13, color: COLOR.textoSuave }}>
        {tarifa.concepto} · {mesa.sillas.length} sillas
      </Typography>

      {tarifa.paquetes.length > 0 ? (
        <>
          <Titulo>¿Para cuántas personas?</Titulo>
          {sinLugar && (
            <Typography sx={{ fontSize: 13, color: "#b45309" }}>Ningún paquete entra en esta mesa: elegí otra.</Typography>
          )}
          {paquetes.map((p) => (
            <Box
              key={p.id}
              onClick={() => onCambiar({ ...valor, idpaquete: p.id })}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: `1px solid ${valor.idpaquete === p.id ? COLOR.acento : COLOR.borde}`,
                bgcolor: valor.idpaquete === p.id ? COLOR.acentoSuave : "transparent",
                borderRadius: 2,
                pr: 1.5,
                mb: 1,
                cursor: "pointer",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Radio size="small" checked={valor.idpaquete === p.id} />
                <Typography sx={{ fontSize: 14 }}>{p.personas} personas</Typography>
              </Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatoPrecio(p.precio)}</Typography>
            </Box>
          ))}
        </>
      ) : (
        <Renglon titulo={`Mesa entera, hasta ${mesa.sillas.length} personas`}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatoPrecio(tarifa.precio)}</Typography>
        </Renglon>
      )}

      {adicionales.length > 0 && (
        <>
          <Titulo>Adicionales</Titulo>
          {adicionales.map((a) => (
            <Renglon
              key={a.idtarifa}
              titulo={a.tarifa.concepto}
              detalle={`${formatoPrecio(a.tarifa.precio)} · hasta ${a.maximo_por_mesa} por mesa`}
            >
              <Cantidad
                valor={valor.adicionales[a.idtarifa] || 0}
                maximo={a.maximo_por_mesa}
                onCambiar={(n) => onCambiar({ ...valor, adicionales: { ...valor.adicionales, [a.idtarifa]: Math.max(0, n) } })}
              />
            </Renglon>
          ))}
        </>
      )}

      {ofrecidas.length > 0 && (
        <>
          <Titulo>Sumá a tu compra</Titulo>
          {ofrecidas.map((t) => (
            <Renglon key={t.id} titulo={t.concepto} detalle={formatoPrecio(t.precio)}>
              <Cantidad
                valor={valor.sugeridas[t.id] || 0}
                maximo={t.limite_persona}
                onCambiar={(n) => onCambiar({ ...valor, sugeridas: { ...valor.sugeridas, [t.id]: Math.max(0, n) } })}
              />
            </Renglon>
          ))}
        </>
      )}

      <Box sx={{ display: "flex", gap: 1, mt: 2.5 }}>
        <Button
          variant="contained"
          onClick={onConfirmar}
          disabled={sinLugar || faltaPaquete}
          sx={{ flex: 1, textTransform: "none", bgcolor: COLOR.acento, borderRadius: 2 }}
        >
          {yaElegida ? "Guardar" : "Agregar mesa"}
        </Button>
        {yaElegida ? (
          <Button onClick={onQuitar} color="error" sx={{ textTransform: "none" }}>
            Quitar mesa
          </Button>
        ) : (
          <Button onClick={onCancelar} sx={{ textTransform: "none", color: COLOR.texto }}>
            Cancelar
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default PanelMesa;
